import React from "react";
import { ArrowRight, Sparkles, ShieldCheck, Truck, Star } from "lucide-react";
import { useLang } from "../store";

export default function Hero() {
  const { lang } = useLang();
  const isEs = lang === "es";

  return (
    <section
      id="top"
      className="relative overflow-hidden border-b-[3px] border-[#0C0A09] bg-[#FAF9F6]"
      data-testid="hero-section"
    >
      {/* subtle dot pattern */}
      <div
        aria-hidden
        className="absolute inset-0 opacity-[0.07] pointer-events-none"
        style={{
          backgroundImage: "radial-gradient(#0C0A09 1.2px, transparent 1.2px)",
          backgroundSize: "22px 22px",
        }}
      />

      <div className="relative max-w-7xl mx-auto px-5 md:px-10 pt-12 md:pt-20 pb-16 md:pb-24">
        {/* Top eyebrow */}
        <div className="flex flex-wrap items-center gap-3 mb-8">
          <span className="nb-badge">
            <Star size={14} strokeWidth={3} />
            {isEs ? "Más de 200 clientes felices" : "200+ happy customers"}
          </span>
          <span className="nb-badge">
            <Truck size={14} strokeWidth={3} />
            {isEs ? "Envío 24h desde España" : "24h shipping from Spain"}
          </span>
          <span className="nb-badge">
            <ShieldCheck size={14} strokeWidth={3} />
            {isEs ? "Pago seguro Stripe" : "Secure Stripe payment"}
          </span>
        </div>

        {/* Headline */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-end mb-12">
          <div className="lg:col-span-8">
            <h1
              data-testid="hero-title"
              className="font-display text-[14vw] sm:text-[10vw] lg:text-[6.5vw] leading-[0.9] tracking-tight"
            >
              {isEs ? (
                <>
                  <span className="block">Productos virales,</span>
                  <span className="block text-[#FF3D7F]" style={{ WebkitTextStroke: "2px #0C0A09" }}>
                    calidad real.
                  </span>
                </>
              ) : (
                <>
                  <span className="block">Viral products,</span>
                  <span className="block text-[#FF3D7F]" style={{ WebkitTextStroke: "2px #0C0A09" }}>
                    real quality.
                  </span>
                </>
              )}
            </h1>
          </div>
          <div className="lg:col-span-4">
            <p className="text-base md:text-lg font-medium leading-relaxed">
              {isEs
                ? "Tu mascota va a estar más feliz que nunca. Productos pensados para hacer tu día (y el suyo) un poco mejor."
                : "Your pet will be happier than ever. Products designed to make your day (and theirs) a little better."}
            </p>
          </div>
        </div>

        {/* Two product cards side-by-side */}
        <div
          className="grid grid-cols-1 md:grid-cols-2 gap-6 lg:gap-8"
          data-testid="hero-products"
        >
          {/* Slushy Cup */}
          <a
            href="#shop"
            data-testid="hero-card-slushy"
            className="group block bg-[#FF3D7F] border-[3px] border-[#0C0A09] shadow-[10px_10px_0_0_#0C0A09] hover:shadow-[14px_14px_0_0_#0C0A09] hover:-translate-y-1 transition-all p-6 md:p-8"
          >
            <div className="flex items-start justify-between mb-5">
              <div>
                <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-white/80">
                  {isEs ? "Producto 01" : "Product 01"}
                </div>
                <h3 className="font-display text-3xl md:text-5xl leading-none text-white mt-1">
                  {isEs ? "Slushy Cup" : "Slushy Cup"}
                </h3>
              </div>
              <div className="bg-[#9BE800] border-[3px] border-[#0C0A09] px-3 py-1.5 rotate-[8deg] shadow-[3px_3px_0_0_#0C0A09]">
                <div className="text-[10px] font-bold line-through opacity-60 leading-none">€15</div>
                <div className="font-display text-2xl leading-none">€5.99</div>
              </div>
            </div>

            <div className="relative h-[220px] md:h-[260px] flex items-center justify-center bg-white/10 border-[3px] border-[#0C0A09] mb-5 overflow-hidden">
              <img
                src="/cups/pink.png"
                alt="Velora Slushy Cup"
                className="h-full w-auto object-contain drop-shadow-[6px_8px_0_#0C0A09] group-hover:scale-105 transition-transform"
              />
            </div>

            <p className="text-white font-medium text-sm md:text-base leading-relaxed mb-5">
              {isEs
                ? "Convierte cualquier jugo en granizado en minutos. Sin máquinas, sin desorden."
                : "Turn any juice into slushy in minutes. No machines, no mess."}
            </p>

            <div className="flex items-center justify-between">
              <div className="flex gap-1.5">
                {["#FF3D7F", "#1FB6FF", "#FFCC00", "#9BE800", "#8B5CF6", "#FF7A1A"].map((c) => (
                  <span key={c} className="w-5 h-5 rounded-full border-[2px] border-[#0C0A09]" style={{ background: c }} />
                ))}
              </div>
              <div className="inline-flex items-center gap-1.5 bg-[#0C0A09] text-white px-4 py-2 text-xs font-bold uppercase tracking-widest border-[3px] border-[#0C0A09] group-hover:bg-white group-hover:text-[#0C0A09] transition-colors">
                {isEs ? "Ver" : "Shop"} <ArrowRight size={14} strokeWidth={3} />
              </div>
            </div>
          </a>

          {/* Pet Station */}
          <a
            href="#pets"
            data-testid="hero-card-pet"
            className="group block bg-[#9BE800] border-[3px] border-[#0C0A09] shadow-[10px_10px_0_0_#0C0A09] hover:shadow-[14px_14px_0_0_#0C0A09] hover:-translate-y-1 transition-all p-6 md:p-8"
          >
            <div className="flex items-start justify-between mb-5">
              <div>
                <div className="text-[10px] font-bold uppercase tracking-[0.2em] opacity-70">
                  {isEs ? "Producto 02 · 🐾 Nuevo" : "Product 02 · 🐾 New"}
                </div>
                <h3 className="font-display text-3xl md:text-5xl leading-none mt-1">
                  {isEs ? "Pet Station" : "Pet Station"}
                </h3>
              </div>
              <div className="bg-[#FF3D7F] text-white border-[3px] border-[#0C0A09] px-3 py-1.5 rotate-[8deg] shadow-[3px_3px_0_0_#0C0A09]">
                <div className="text-[10px] font-bold line-through opacity-70 leading-none">€69.99</div>
                <div className="font-display text-2xl leading-none">€49.99</div>
              </div>
            </div>

            <div className="relative h-[220px] md:h-[260px] bg-white border-[3px] border-[#0C0A09] mb-5 overflow-hidden">
              <img
                src="/pets/pet-lifestyle-dog-cat.jpg"
                alt="Velora Pet Station"
                className="w-full h-full object-cover group-hover:scale-105 transition-transform"
              />
            </div>

            <p className="font-medium text-sm md:text-base leading-relaxed mb-5">
              {isEs
                ? "Bebedero + comedero 2-en-1. Tu mascota nunca se queda sin nada, ni cuando viajas."
                : "Water + food dispenser 2-in-1. Your pet never runs out, even when traveling."}
            </p>

            <div className="flex items-center justify-between">
              <div className="flex gap-1.5">
                {["#F5C8C8", "#B8BCC2", "#F5F4F0", "#B5CFB0"].map((c) => (
                  <span key={c} className="w-5 h-5 rounded-full border-[2px] border-[#0C0A09]" style={{ background: c }} />
                ))}
              </div>
              <div className="inline-flex items-center gap-1.5 bg-[#0C0A09] text-white px-4 py-2 text-xs font-bold uppercase tracking-widest border-[3px] border-[#0C0A09] group-hover:bg-white group-hover:text-[#0C0A09] transition-colors">
                {isEs ? "Ver" : "Shop"} <ArrowRight size={14} strokeWidth={3} />
              </div>
            </div>
          </a>
        </div>

        {/* Bottom mini-CTA */}
        <div className="mt-10 flex flex-wrap items-center justify-center gap-2 text-xs font-bold uppercase tracking-widest opacity-70">
          <Sparkles size={14} strokeWidth={3} />
          {isEs ? "Selección de productos virales · Envío desde España" : "Curated viral products · Shipped from Spain"}
        </div>
      </div>
    </section>
  );
}
